/**
 * SectorSelector — fija el sector Kaptar de un expediente idea-first.
 *
 * Formulador slice 2 (ADR-021): el rango de presupuesto data-grounded del
 * módulo `presupuesto` requiere un sector SGR real. Los expedientes que
 * nacen desde una idea (sin convocatoria) arrancan con `sector: 'generico'`
 * — este componente se muestra en la sección Preparación mientras ese sea
 * el caso, y desaparece apenas el funcionario fija un sector real.
 *
 * Autocontenido: dueño de su propia mutación (`updateProject`) e invalida
 * tanto el expediente (`project-detail`) como la formulación
 * (`formulation-by-project`) al guardar — así el chip de sector del header
 * y el hint del módulo presupuesto se refrescan solos.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { KAPTAR_SECTOR_OPTIONS, updateProject } from "@/lib/api/projects";

interface SectorSelectorProps {
  projectId: string;
  municipality: string;
}

export function SectorSelector({ projectId, municipality }: SectorSelectorProps) {
  const queryClient = useQueryClient();
  const [sector, setSector] = useState("");

  const mutation = useMutation({
    mutationFn: (value: string) => updateProject(projectId, municipality, { sector: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["project-detail", projectId] });
      void queryClient.invalidateQueries({
        queryKey: ["formulation-by-project", projectId],
      });
    },
  });

  return (
    <div
      data-testid="sector-selector"
      className="flex flex-col gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4"
    >
      <p className="text-xs text-slate-600">
        Define el sector del proyecto para obtener el rango de presupuesto de referencia.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="sector-selector-select">
          Sector del proyecto
        </label>
        <select
          id="sector-selector-select"
          data-testid="sector-selector-select"
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
        >
          <option value="">Selecciona un sector...</option>
          {KAPTAR_SECTOR_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="sector-selector-save-btn"
          disabled={!sector || mutation.isPending}
          onClick={() => mutation.mutate(sector)}
          className="min-h-[44px] rounded-lg bg-cta-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-600 focus:ring-2 focus:ring-cta-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? "Guardando..." : "Guardar sector"}
        </button>
      </div>
      {mutation.isError && (
        <p data-testid="sector-selector-error" className="text-xs text-red-700">
          {mutation.error.message}
        </p>
      )}
    </div>
  );
}
